import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Sparkles } from 'lucide-react';
import type { ReactNode } from 'react';
import { Badge, Button, type Tone } from '@zt/ui';
import { api } from '../../lib/api';
import { useI18n, useT } from '../../lib/i18n';
import { usePermissions } from '../../lib/session';

/**
 * Yapay zekâ ekranlarının ortak parçaları (Faz 9): erişilebilirlik durumu, USD gösterimi, "Yapay zekâ ile" düğmesi.
 * Düğme, sağlayıcı bağlı değilse "Kurulum Gerekli", sahip açmadıysa "Kapalı", sınır dolduysa "Sınır doldu" yazar ve
 * çalışmaz (sahte üretim yok).
 */

export type AiState = 'ready' | 'not_configured' | 'disabled' | 'budget_exhausted' | 'daily_limit';

export interface AiAvailability {
  text: AiState;
  image: AiState;
  enabled: boolean;
  remainingCents: number;
  requestsLeftToday: number;
  supportDrafts: boolean;
}

export function useAiAvailability() {
  const { can } = usePermissions();
  return useQuery({
    queryKey: ['ai', 'availability'],
    queryFn: () => api.get<AiAvailability>('/v1/admin/ai/availability'),
    enabled: can('ai.generate'),
    staleTime: 30_000,
    // Entegrasyonlar ekranında anahtar girildikten sonra yapay zekâ ekranına dönülünce hemen güncellensin.
    refetchOnMount: 'always',
  });
}

const TAG: Record<string, string> = { tr: 'tr-TR', en: 'en-GB', de: 'de-DE', ar: 'ar' };

/** Mikro USD tam sayısını ondalık metne çevirir (kayan nokta yok): 5400 → "0.005400". */
export function microsToDecimal(micros: number): string {
  const neg = micros < 0;
  const s = String(Math.abs(Math.trunc(micros))).padStart(7, '0');
  return `${neg ? '-' : ''}${s.slice(0, -6)}.${s.slice(-6)}`;
}

/** Mikro USD → düzenleme alanı metni ("0,25"; sondaki sıfırlar atılır, Türkçede ondalık virgül). */
export function microsToInput(micros: number | null | undefined, locale: string): string {
  if (micros === null || micros === undefined) return '';
  const s = microsToDecimal(micros).replace(/0+$/, '').replace(/\.$/, '');
  return locale === 'en' ? s : s.replace('.', ',');
}

/** Ekranda USD: en az 2, en çok 4 ondalık ("0,0054 $"). Yalnızca gösterim içindir. */
export function useUsd() {
  const { locale } = useI18n();
  const nf = new Intl.NumberFormat(TAG[locale] ?? 'tr-TR', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
  return (micros: number | null | undefined) =>
    micros === null || micros === undefined ? '—' : nf.format(microsToDecimal(micros) as `${number}`);
}

/** Kullanıcının yazdığı USD tutarını ("2,50", "0.125") mikro USD'ye çevirir; okunamazsa null. */
export function usdToMicros(input: string): number | null {
  const t = input.trim().replace(/\s/g, '').replace(',', '.');
  const m = /^(\d{1,7})(?:\.(\d{1,6}))?$/.exec(t);
  if (!m) return null;
  return Number(m[1]) * 1_000_000 + Number((m[2] ?? '').padEnd(6, '0'));
}

export const STATE_TONE: Record<AiState, Tone> = {
  ready: 'moss',
  not_configured: 'slate',
  disabled: 'slate',
  budget_exhausted: 'amber',
  daily_limit: 'amber',
};

export function AiStateBadge({ state }: { state: AiState }) {
  const t = useT();
  return <Badge tone={STATE_TONE[state]}>{t(`ai.availability.${state}`)}</Badge>;
}

/**
 * Yapay zekâ ile üretim düğmesi. Hazır değilse devre dışıdır ve nedenini gösterir; "Kurulum Gerekli" durumunda yetkisi
 * olana Entegrasyonlar bağlantısı verilir.
 */
export function AiButton({
  kind = 'text',
  onClick,
  loading,
  children,
  size = 'sm',
}: {
  kind?: 'text' | 'image';
  onClick: () => void;
  loading?: boolean;
  children: ReactNode;
  size?: 'sm' | 'md';
}) {
  const t = useT();
  const { can } = usePermissions();
  const q = useAiAvailability();
  if (!can('ai.generate')) return null;
  const state = q.data?.[kind];
  const ready = state === 'ready';
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <Button
        size={size}
        variant="secondary"
        disabled={!ready || q.isLoading}
        loading={loading}
        onClick={onClick}
      >
        <Sparkles className="h-4 w-4" aria-hidden />
        {children}
      </Button>
      {state && !ready && (
        <span className="inline-flex items-center gap-2 text-sm text-ink-3">
          <AiStateBadge state={state} />
          {state === 'not_configured' && can('org.integration.read') && (
            <Link to="/entegrasyonlar" className="underline underline-offset-2">
              {t('ai.settings.toIntegrations')}
            </Link>
          )}
        </span>
      )}
    </span>
  );
}
